<template>
  <div id="av-popup" :class="{ show: visible }" @mouseenter="cancelHide" @mouseleave="hide(120)">
    <div class="avp-head">
      <div>
        <img
          v-if="data?.thumbnail"
          :src="data.thumbnail"
          :alt="data.name"
          class="avp-img"
          @error="(e: Event) => (e.target as HTMLImageElement).style.display='none'"
        />
        <div v-else class="avp-avatar" :style="{ background: nameColor }">{{ nameInitials }}</div>
      </div>
      <div>
        <div class="avp-name">{{ data?.name || '—' }}</div>
        <div class="avp-role">{{ data?.role || data?.type || '—' }}</div>
      </div>
    </div>
    <div class="avp-body">
      <div v-if="data?.email" class="avp-row">
        <svg class="avp-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
          <polyline points="22,6 12,13 2,6"/>
        </svg>
        <span class="avp-info">{{ data.email }}</span>
      </div>
      <div v-if="data?.phone" class="avp-row avp-row--phone">
        <svg class="avp-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M22 16.92v3a2 2 0 0 1-2.18 2A19.8 19.8 0 0 1 3.08 4.18 2 2 0 0 1 5.05 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L9.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 23 17z"/>
        </svg>
        <span class="avp-info">{{ fmtPhone(data.phone) }}</span>
        <button
          type="button"
          class="avp-phone-copy"
          :class="{ 'is-copied': phoneCopied }"
          @click.stop="copyPhone"
          :title="phoneCopied ? 'Copiado!' : 'Copiar número'"
        >
          <svg v-if="phoneCopied" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
          <svg v-else width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
            <rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
          </svg>
        </button>
      </div>
      <div v-if="data?.team" class="avp-row">
        <svg class="avp-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
          <circle cx="9" cy="7" r="4"/>
        </svg>
        <span class="avp-info">{{ data.team }}</span>
      </div>
      <div v-if="data?.channel" class="avp-row">
        <svg class="avp-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
        </svg>
        <span class="avp-info">{{ data.channel }}</span>
      </div>
      <div v-if="data?.status !== undefined" class="avp-row">
        <svg class="avp-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="10"/>
        </svg>
        <span class="avp-badge" :class="badgeClass">{{ data.status }}</span>
      </div>
      <div v-if="data?.convs !== undefined" class="avp-row">
        <svg class="avp-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <rect x="3" y="3" width="18" height="18" rx="2"/>
          <line x1="9" y1="9" x2="9" y2="15"/>
        </svg>
        <span class="avp-info">{{ data.convs }} conversa(s)</span>
      </div>
      <div v-if="data?.openConvs && data.openConvs > 0" class="avp-row">
        <svg class="avp-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <path d="M3 18v-6a9 9 0 0 1 18 0v6"/>
          <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z"/>
        </svg>
        <span class="avp-atend">em atendimento ({{ data.openConvs }})</span>
      </div>
      <div v-if="data?.labels?.length" class="avp-row">
        <svg class="avp-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/>
          <line x1="7" y1="7" x2="7.01" y2="7"/>
        </svg>
        <span class="avp-info">{{ (data.labels || []).map(l => l.replace(/_/g, ' ')).join(', ') }}</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import type { PopupData } from '@/types/dashboard'
import { avatarColor, initials } from '@/composables/useHelpers'
import { fmtPhone } from '@/utils/contactsFormat'

const visible     = ref(false)
const data        = ref<PopupData | null>(null)
const posX        = ref(0)
const posY        = ref(0)
const phoneCopied = ref(false)
let timer: ReturnType<typeof setTimeout> | null = null

const nameColor    = computed(() => avatarColor(data.value?.name || ''))
const nameInitials = computed(() => initials(data.value?.name || ''))
const badgeClass   = computed(() => {
  const s = data.value?.status
  return { online: 'online', offline: 'offline', busy: 'busy' }[s || ''] || 'offline'
})

function show(popupData: PopupData, anchor: HTMLElement) {
  if (timer) clearTimeout(timer)
  data.value    = popupData
  visible.value = true

  const rect = anchor.getBoundingClientRect()
  const pw = 260, ph = 200
  let lx = rect.right + 8, ly = rect.top
  if (lx + pw > window.innerWidth)  lx = rect.left - pw - 8
  if (ly + ph > window.innerHeight) ly = window.innerHeight - ph - 8
  posX.value = lx
  posY.value = Math.max(8, ly)

  const el = document.getElementById('av-popup')
  if (el) { el.style.left = posX.value + 'px'; el.style.top = posY.value + 'px' }
}

function hide(delay = 200) {
  if (timer) clearTimeout(timer)
  timer = setTimeout(() => { visible.value = false }, delay)
}

function cancelHide() {
  if (timer) clearTimeout(timer)
}

async function copyPhone() {
  const phone = data.value?.phone
  if (!phone) return
  await navigator.clipboard.writeText(phone)
  phoneCopied.value = true
  setTimeout(() => { phoneCopied.value = false }, 2000)
}

defineExpose({ show, hide })
</script>
